import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useNotify } from 'react-admin';
import { ArrowLeft, Clock, User, FileText, GitBranch, ExternalLink } from 'lucide-react';
import { getDocumentById, getDocumentVersionById } from '../../services/documentService.ts';
import { Document, DocumentVersion } from '../../types/document.ts';

const statusColor = (status?: string) => {
  switch (status) {
    case 'Approved': return 'bg-green-100 text-green-800';
    case 'Review':   return 'bg-blue-100 text-blue-800';
    case 'Obsolete': return 'bg-gray-100 text-gray-600';
    default:         return 'bg-yellow-100 text-yellow-800';
  }
};

const statusLabel = (status?: string) => {
  if (status === 'Review') return 'Under Review';
  return status || 'Draft';
};

export default function DocumentVersionPreview() {
  const { docId, versionId } = useParams<{ docId: string; versionId: string }>();
  const navigate = useNavigate();
  const notify = useNotify();

  const [doc, setDoc] = useState<Document | null>(null);
  const [version, setVersion] = useState<DocumentVersion | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!docId || !versionId) return;
    (async () => {
      setLoading(true);
      try {
        const [docData, versionData] = await Promise.all([
          getDocumentById(docId),
          getDocumentVersionById(versionId),
        ]);
        setDoc(docData);
        setVersion(versionData);
      } catch (err: any) {
        notify(err?.message || 'Failed to load version', { type: 'warning' });
      } finally {
        setLoading(false);
      }
    })();
  }, [docId, versionId, notify]);

  const InfoRow = ({ label, value }: { label: string; value?: string | null }) => (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{label}</span>
      <span className="text-sm text-gray-900">{value || '—'}</span>
    </div>
  );

  return (
    <div className="w-full h-full overflow-auto flex flex-col bg-transparent">
      <div className="flex-1 px-2 pt-2 pb-4 overflow-auto">

        {loading ? (
          <div className="min-h-[300px] rounded-2xl border border-gray-200 bg-white flex items-center justify-center text-slate-500 text-sm shadow-sm">
            Loading…
          </div>
        ) : !version ? (
          <div className="min-h-[300px] rounded-2xl border border-gray-200 bg-white flex flex-col items-center justify-center gap-3 shadow-sm">
            <FileText className="w-10 h-10 text-gray-300" />
            <p className="text-gray-500 text-sm">Version not found.</p>
            <button onClick={() => navigate(-1)} className="text-blue-600 text-sm font-semibold hover:underline">
              Go back
            </button>
          </div>
        ) : (
          <div className="space-y-3">

            {/* ── Card 1: Header + File ── */}
            <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
              <button
                onClick={() => navigate(-1)}
                className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-slate-900 mb-4"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </button>

              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <GitBranch className="w-4 h-4 text-gray-400" />
                    <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                      Version {version.version}
                    </span>
                    {version.isActive && (
                      <span className="px-2 py-0.5 bg-blue-600 text-white rounded-full text-xs font-medium">Current</span>
                    )}
                  </div>
                  <h1 className="text-xl font-bold text-slate-900">{doc?.title || '—'}</h1>
                  <p className="text-sm text-slate-500 mt-0.5">{doc?.documentNumber || '—'}</p>
                </div>

                <div className="flex items-center gap-3 flex-shrink-0">
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${statusColor(version.status)}`}>
                    {statusLabel(version.status)}
                  </span>
                  {version.fileUrl && (
                    <a
                      href={version.fileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 transition-colors"
                    >
                      <ExternalLink className="w-4 h-4" />
                      Open File
                    </a>
                  )}
                </div>
              </div>
            </div>

            {/* ── Card 2: All Details ── */}
            <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm space-y-5">
              {/* Basic info grid */}
              <div>
                <h3 className="text-base font-semibold text-gray-900 mb-4">Basic Information</h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-5">
                  <InfoRow label="Document Number" value={doc?.documentNumber} />
                  <InfoRow label="Category" value={doc?.category} />
                  <InfoRow label="Version" value={version.version} />
                  <InfoRow label="Status" value={statusLabel(version.status)} />
                  <InfoRow label="Owner" value={version.ownerName || doc?.ownerName} />
                  <InfoRow
                    label="Created Date"
                    value={version.createdAt ? new Date(version.createdAt).toLocaleDateString('en-GB') : undefined}
                  />
                  <InfoRow label="Created By" value={version.createdBy} />
                </div>
              </div>

              {/* Divider */}
              <div className="border-t border-gray-100" />

              {/* Description */}
              {doc?.description && (
                <>
                  <div>
                    <h3 className="text-sm font-semibold text-gray-700 mb-1">Description</h3>
                    <p className="text-sm text-gray-600 whitespace-pre-wrap">{doc.description}</p>
                  </div>
                  <div className="border-t border-gray-100" />
                </>
              )}

              {/* Version Change Notes */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-1">Version Change Notes</h3>
                <p className="text-sm text-gray-600 whitespace-pre-wrap">
                  {version.versionChanges || version.changes || '—'}
                </p>
              </div>

              {/* Meta footer */}
              <div className="border-t border-gray-100 pt-3 flex items-center gap-4 text-xs text-gray-400">
                <span className="flex items-center gap-1">
                  <Clock className="w-3.5 h-3.5" />
                  {version.createdAt ? new Date(version.createdAt).toLocaleString('en-GB') : '—'}
                </span>
                <span className="flex items-center gap-1">
                  <User className="w-3.5 h-3.5" />
                  {version.createdBy || '—'}
                </span>
              </div>
            </div>

          </div>
        )}
      </div>
    </div>
  );
}
